import React, { useState, useCallback } from 'react';
import { editImageWithPrompt } from '../services/geminiService';
import { Spinner } from './Spinner';

export const ImageEditor = ({ image, onImageUpdate, onBack }) => {
  const [prompt, setPrompt] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleApplyEdit = useCallback(async () => {
    if (!prompt.trim()) return;
    setIsLoading(true);
    setError(null);
    try {
      const updatedImage = await editImageWithPrompt(image, prompt);
      onImageUpdate(updatedImage);
    } catch (err) {
      setError('Failed to edit image. Please try again.');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  }, [prompt, image, onImageUpdate]);

  return (
    <div className="w-full max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 mt-8">
      <button
        onClick={onBack}
        className="mb-6 text-[#F06B32] hover:text-[#e55a27] font-semibold flex items-center transition-colors duration-300"
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
        </svg>
        Back to Gallery
      </button>

      <div className="bg-[#270b79] bg-opacity-50 p-6 rounded-2xl shadow-2xl flex flex-col lg:flex-row gap-8 border border-[#F06B32] border-opacity-30">
        <div className="lg:w-1/2 relative">
          <img src={image.base64} alt="Image to edit" className="rounded-xl w-full object-contain shadow-lg" />
          {isLoading && (
            <div className="absolute inset-0 bg-black bg-opacity-70 flex items-center justify-center rounded-xl">
              <div className="text-center">
                <Spinner className="w-12 h-12 mx-auto mb-4" />
                <p className="text-white font-medium">Applying edits...</p>
              </div>
            </div>
          )}
        </div>
        <div className="lg:w-1/2 flex flex-col">
          <h2 className="text-2xl font-bold text-white mb-4">Edit Image with AI</h2>
          <p className="text-gray-300 mb-6 text-sm leading-relaxed">
            Describe the changes you want to make. For example, "add a retro filter" or "change the background to a beach".
          </p>
          <div className="flex-grow mb-6">
            <textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder="e.g., Make the lighting more dramatic, change background to sunset, add professional shadows..."
              className="w-full h-full min-h-[120px] bg-[#1a074f] border-2 border-[#270b79] text-white text-sm rounded-xl focus:ring-2 focus:ring-[#F06B32] focus:border-[#F06B32] block p-4 transition-all duration-300"
              disabled={isLoading}
            />
          </div>
          {error && (
            <div className="mb-4 p-3 bg-red-900 bg-opacity-50 border border-red-700 rounded-lg">
              <p className="text-red-300 text-center text-sm">{error}</p>
            </div>
          )}
          <button
            onClick={handleApplyEdit}
            disabled={isLoading || !prompt.trim()}
            className="w-full bg-[#F06B32] hover:bg-[#e55a27] disabled:bg-gray-600 disabled:cursor-not-allowed text-white font-bold py-4 px-6 rounded-xl transition-all duration-300 ease-in-out transform hover:scale-105 flex items-center justify-center shadow-lg"
          >
            {isLoading && <Spinner className="w-5 h-5 mr-3" />}
            {isLoading ? 'Applying AI Magic...' : 'Apply Edit'}
          </button>
        </div>
      </div>
    </div>
  );
};